import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { RegisterOrganizerResponse } from '@radevents/shared';
import PublicHeader from '../components/Layout/PublicHeader';
import StaticPageTopbar from '../components/Layout/StaticPageTopbar';
import { useAuth } from '../contexts/AuthContext';
import { useOrganizerPortal } from '../contexts/OrganizerPortalContext';
import organizerAccessService from '../services/organizerAccessService';
import { organizerDomainMatches } from '../components/organizer/claimIntent';
import { trackOrganizerFunnel } from '../services/trackingService';

// ── „Veranstalter eintragen" ─────────────────────────────────────────────────────────────────
// Der Weg für Vereine und Anbieter, die im Verzeichnis fehlen (docs/monetarisierung.md,
// 11.09.2026). Bis dahin entstand ein neuer Veranstalter nur über die Redaktion oder als
// Nebenprodukt eines Event-Vorschlags; wer sich selbst eintragen wollte, fand keinen Knopf.
//
// Ein Konto ist dafür nicht nötig: wer einen fremden Verein kennt, schlägt ihn vor. Mit Konto
// kommt die Verwaltung dazu. Drei Ausgänge, die der Server entscheidet
// (`/organizer-edits/register`): steht schon da (dann führt der Weg zum Antrag dort), sofort
// angelegt und verknüpft (die bestätigte E-Mail-Adresse passte zu Website oder Vereinsadresse)
// oder als Vorschlag in der Prüfung.

const FONT_DISPLAY = "'Archivo',sans-serif";
const FONT_BODY = "'Hanken Grotesk',sans-serif";
const FONT_MONO = "'Space Mono',monospace";
const INK = '#2C2C2C';
const MUTED = '#6A6A6A';
const BORDER = '#E5E1E1';
const BERRY = '#922C57';

const PATH = '/veranstalter/eintragen';

const CSS = `
.or-register-form { background:#fff; border:1px solid ${BORDER}; border-radius:16px; padding:26px; display:grid; gap:16px; }
.or-register-location { display:grid; grid-template-columns:120px 1fr; gap:12px; }
.or-register-contact { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
@media (max-width: 520px) {
  .or-register-form { padding:20px 16px; }
  .or-register-location, .or-register-contact { grid-template-columns:1fr; }
}
`;

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', marginTop: 6, border: `1px solid ${BORDER}`, borderRadius: 10,
  padding: '11px 12px', fontFamily: FONT_BODY, fontSize: 15, color: INK, background: '#fff',
};
const labelStyle: React.CSSProperties = {
  fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.1em', color: '#9A9494', textTransform: 'uppercase',
};
const btn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: FONT_BODY, fontWeight: 700, fontSize: 15,
  padding: '13px 22px', borderRadius: 11, textDecoration: 'none', border: `1px solid ${BORDER}`,
  background: '#fff', color: INK, cursor: 'pointer',
};
const btnPrimary: React.CSSProperties = { ...btn, background: BERRY, borderColor: BERRY, color: '#fff' };

/** Was nach dem Absenden auf der Seite steht — je Ausgang des Servers. */
export const Outcome: React.FC<{
  result: RegisterOrganizerResponse;
  name: string;
  anonymous?: boolean;
  offerQuery?: string;
}> = ({ result, name, anonymous, offerQuery = '' }) => {
  if (result.existingOrganizerId != null) {
    return (
      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 16, padding: 26 }}>
        <div style={labelStyle}>Steht schon bei uns</div>
        <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 22, color: INK, margin: '8px 0 10px' }}>
          {result.existingOrganizerName ?? name} gibt es schon im Verzeichnis.
        </h2>
        <p style={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.55, color: MUTED, margin: '0 0 18px' }}>
          {anonymous
            ? 'Ein zweites Mal anlegen hilft niemandem: die Events hängen an der vorhandenen Veranstalterseite. Fehlt dort etwas, schlag es direkt auf der Seite vor.'
            : 'Ein zweites Mal anlegen hilft niemandem: die Events hängen an der vorhandenen Veranstalterseite. Übernimm stattdessen die Seite, die es schon gibt. Passt deine E-Mail-Adresse zum Veranstalter, geht das sofort.'}
        </p>
        {anonymous ? (
          <Link to={`/veranstalter/${result.existingOrganizerId}${offerQuery}`} style={btnPrimary}>Zur Seite</Link>
        ) : (
          <Link to={`/veranstalter/${result.existingOrganizerId}?antrag=1${offerQuery ? `&${offerQuery.slice(1)}` : ''}`} style={btnPrimary}>
            Zur Seite und Zugang beantragen
          </Link>
        )}
      </div>
    );
  }
  if (result.organizerId != null) {
    return (
      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 16, padding: 26 }}>
        <div style={labelStyle}>Eingetragen</div>
        <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 22, color: INK, margin: '8px 0 10px' }}>
          {name} steht jetzt bei uns, und du verwaltest die Veranstalterseite.
        </h2>
        <p style={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.55, color: MUTED, margin: '0 0 18px' }}>
          Deine E-Mail-Adresse passte zur angegebenen Website oder Vereinsadresse, deshalb musste
          niemand prüfen. Vervollständige jetzt die Stammdaten und trag dein erstes Event ein.
        </p>
        <Link to={`/veranstalter/${result.organizerId}${offerQuery}`} style={btnPrimary}>Zu deiner Veranstalterseite</Link>
      </div>
    );
  }
  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 16, padding: 26 }}>
      <div style={labelStyle}>Eingereicht</div>
      <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 22, color: INK, margin: '8px 0 10px' }}>
        {anonymous ? `Danke, wir prüfen ${name}.` : `Wir prüfen ${name} und melden uns.`}
      </h2>
      <p style={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.55, color: MUTED, margin: '0 0 18px' }}>
        {anonymous
          ? 'Jemand von uns sieht sich den Vorschlag kurz an, dann steht der Veranstalter im Verzeichnis.'
          : 'Deine E-Mail-Adresse passt nicht zur angegebenen Website oder Vereinsadresse, deshalb sieht sich jemand von uns die Angaben kurz an. Danach bekommst du eine E-Mail und verwaltest die Veranstalterseite selbst.'}
      </p>
      <Link to="/veranstalter" style={btn}>Zurück zu den Veranstaltern</Link>
    </div>
  );
};

const OrganizerRegister: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { reload } = useOrganizerPortal();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const offer = searchParams.get('angebot');
  const offerQuery = offer === 'premium' || offer === 'promotion' ? `?angebot=${offer}` : '';
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [note, setNote] = useState('');
  const [submitterName, setSubmitterName] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<RegisterOrganizerResponse | null>(null);

  /**
   * Dieselbe Regel wie im Backend, hier nur als Hinweis: passt die Adresse des Kontos zur
   * Website, geht es sofort — das sagt der Satz unter dem Feld, bevor jemand abschickt.
   */
  const matches = organizerDomainMatches(user?.email, user?.emailVerified, website, email);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 3) {
      toast.error('Bitte gib den Namen des Veranstalters an.');
      return;
    }
    setSending(true);
    try {
      const created = await organizerAccessService.register({
        name: name.trim(),
        website: website.trim() || undefined,
        email: email.trim() || undefined,
        postalCode: postalCode.trim() || undefined,
        city: city.trim() || undefined,
        note: note.trim() || undefined,
        submitterName: isAuthenticated ? undefined : submitterName.trim() || undefined,
        submitterEmail: isAuthenticated ? undefined : submitterEmail.trim() || undefined,
      });
      trackOrganizerFunnel('organizer_registration_submitted', {
        offer: offer === 'premium' || offer === 'promotion' ? offer : 'free',
        result: created.organizerId != null ? 'automatic' : created.existingOrganizerId != null ? 'existing' : 'review',
      });
      setResult(created);
      // Sofort verknüpft: Kopfleiste und Veranstalter-Bereich sollen den neuen Veranstalter kennen.
      if (created.organizerId != null) reload();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Das hat nicht geklappt. Bitte versuch es noch einmal.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>Veranstalter eintragen | Radsport Events</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="hide-mobile"><PublicHeader /></div>
      <StaticPageTopbar label="// VERANSTALTER EINTRAGEN" />
      <style>{CSS}</style>

      <div style={{ flex: 1, width: '100%', boxSizing: 'border-box', padding: '24px 16px 60px', maxWidth: 640, margin: '0 auto' }}>
        <Link to="/veranstalter" style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.12em', color: BERRY, textDecoration: 'none' }}>
          ‹ VERANSTALTER
        </Link>
        <h1 style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 30, letterSpacing: '-0.015em', color: INK, margin: '10px 0 8px' }}>
          Veranstalter eintragen
        </h1>
        <p style={{ fontFamily: FONT_BODY, fontSize: 15.5, lineHeight: 1.55, color: MUTED, margin: '0 0 22px' }}>
          Für Vereine und Anbieter, die noch nicht im Verzeichnis stehen. Kostenlos, ein Konto ist
          nicht nötig.
        </p>

        {/* Kein Konto nötig: wer einen fremden Verein kennt, schlägt ihn vor. Nur wer die Seite
            danach verwalten will, braucht eines — das steht hier als Angebot, nicht als Hürde. */}
        {!isAuthenticated && !result && (
          <div style={{ background: 'rgba(146,44,87,.06)', border: '1px solid rgba(146,44,87,.18)', borderRadius: 14, padding: '14px 18px', marginBottom: 16, fontFamily: FONT_BODY, fontSize: 14.5, lineHeight: 1.55, color: INK }}>
            <strong>Du gehörst selbst zum Veranstalter?</strong> Dann{' '}
            <button type="button" onClick={() => navigate(`/login?redirect=${encodeURIComponent(PATH + offerQuery)}&veranstalter=1`)}
              style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 700, color: BERRY, cursor: 'pointer', textDecoration: 'underline' }}>
              melde dich an oder leg ein Konto an
            </button>
            , am besten mit einer E-Mail-Adresse auf eurer Domain. Danach verwaltest du die
            Veranstalterseite selbst: Stammdaten, Logo und Events.
          </div>
        )}

        {result ? (
          <Outcome result={result} name={name.trim()} anonymous={!isAuthenticated} offerQuery={offerQuery} />
        ) : (
          <form onSubmit={submit} className="or-register-form">
            <label>
              <span style={labelStyle}>Name des Veranstalters <span aria-hidden style={{ color: BERRY }}>*</span></span>
              <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} required
                placeholder="RSV Beispiel e.V. oder Beispiel Events GmbH" />
            </label>
            <label>
              <span style={labelStyle}>Website</span>
              <input value={website} onChange={e => setWebsite(e.target.value)} style={inputStyle}
                placeholder="https://www.rsv-beispiel.de" inputMode="url" />
              {isAuthenticated && (
                <span style={{ display: 'block', fontFamily: FONT_BODY, fontSize: 12.5, color: matches ? '#1F8A5B' : MUTED, marginTop: 5 }}>
                  {!user?.emailVerified
                    ? 'Bestätige zuerst die E-Mail-Adresse deines Kontos. Danach kann eine passende Domain den Zugang sofort freischalten.'
                    : matches
                    ? `Deine Adresse ${user?.email} passt zur Website. Die Veranstalterseite ist sofort da.`
                    : 'Passt deine bestätigte E-Mail-Adresse zur Website oder Veranstalter-Adresse, ist die Seite sofort da. Sonst prüfen wir kurz.'}
                </span>
              )}
            </label>
            <label>
              <span style={labelStyle}>E-Mail des Veranstalters</span>
              <input value={email} onChange={e => setEmail(e.target.value)} style={inputStyle}
                placeholder="info@rsv-beispiel.de" type="email" />
            </label>
            <div className="or-register-location">
              <label>
                <span style={labelStyle}>PLZ</span>
                <input value={postalCode} onChange={e => setPostalCode(e.target.value)} style={inputStyle} inputMode="numeric" />
              </label>
              <label>
                <span style={labelStyle}>Ort</span>
                <input value={city} onChange={e => setCity(e.target.value)} style={inputStyle} />
              </label>
            </div>
            {isAuthenticated ? (
              <label>
                <span style={labelStyle}>Deine Rolle <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span></span>
                <input value={note} onChange={e => setNote(e.target.value)} style={inputStyle}
                  placeholder="1. Vorsitzende, Orga-Team, Geschäftsführung" />
              </label>
            ) : (
              <div className="or-register-contact">
                <label>
                  <span style={labelStyle}>Dein Name <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span></span>
                  <input value={submitterName} onChange={e => setSubmitterName(e.target.value)} style={inputStyle} />
                </label>
                <label>
                  <span style={labelStyle}>Deine E-Mail <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span></span>
                  <input value={submitterEmail} onChange={e => setSubmitterEmail(e.target.value)} style={inputStyle} type="email" />
                </label>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button type="submit" style={{ ...btnPrimary, opacity: sending ? 0.6 : 1 }} disabled={sending}>
                {sending ? 'Wird eingetragen…' : 'Veranstalter eintragen'}
              </button>
              <span style={{ fontFamily: FONT_BODY, fontSize: 13, color: MUTED }}>
                {isAuthenticated
                  ? 'Alles Weitere pflegst du danach auf deiner Veranstalterseite.'
                  : 'Wir prüfen den Vorschlag, dann steht der Veranstalter im Verzeichnis.'}
              </span>
            </div>
          </form>
        )}
      </div>

    </div>
  );
};

export default OrganizerRegister;
